import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

// Dynamic theme configuration from environment variables
function getThemeConfig(clientTheme) {
  // Default configuration fallback
  const defaultConfig = {
    name: 'Henddu Air Quality Monitoring',
    shortName: 'Henddu',
    logoPath: '/images/logos/logo-henddu.png',
    faviconPath: '/logo.png',
    features: {
      advancedAnalytics: true,
      customReports: true,
      multiLanguage: true,
      correlationAnalysis: true,
      seasonalAnalysis: true,
      dataExport: true,
      realTimeAlerts: true
    },
    colors: {
      primary: '#0D7377',
      secondary: '#40A578'
    }
  };

  // Build configuration from environment variables
  const config = {
    name: import.meta.env.VITE_APP_TITLE || defaultConfig.name,
    shortName: import.meta.env.VITE_BRAND_NAME || defaultConfig.shortName,
    logoPath: import.meta.env.VITE_CLIENT_LOGO_URL || defaultConfig.logoPath,
    faviconPath: import.meta.env.VITE_CLIENT_FAVICON || defaultConfig.faviconPath,
    supportEmail: import.meta.env.VITE_SUPPORT_EMAIL,
    companyUrl: import.meta.env.VITE_COMPANY_URL,
    features: {
      advancedAnalytics: import.meta.env.VITE_FEATURE_ADVANCED_ANALYTICS === 'true',
      customReports: import.meta.env.VITE_FEATURE_REPORTING === 'true',
      multiLanguage: import.meta.env.VITE_FEATURE_MULTI_LANGUAGE !== 'false', // Default true
      correlationAnalysis: import.meta.env.VITE_FEATURE_CORRELATION_ANALYSIS !== 'false', // Default true
      seasonalAnalysis: import.meta.env.VITE_FEATURE_SEASONAL_ANALYSIS !== 'false', // Default true
      dataExport: import.meta.env.VITE_FEATURE_DATA_EXPORT !== 'false', // Default true
      realTimeAlerts: import.meta.env.VITE_FEATURE_ALERTS === 'true',
      customBranding: import.meta.env.VITE_FEATURE_CUSTOM_BRANDING === 'true',
      whiteLabel: import.meta.env.VITE_FEATURE_WHITE_LABEL === 'true',
      ecoInsights: import.meta.env.VITE_FEATURE_ECO_INSIGHTS === 'true'
    },
    colors: {
      // These will be dynamically read from CSS variables, but keep fallbacks
      primary: defaultConfig.colors.primary,
      secondary: defaultConfig.colors.secondary
    },
    api: {
      baseUrl: import.meta.env.VITE_API_BASE_URL,
      version: import.meta.env.VITE_API_VERSION || 'v1'
    },
    map: {
      centerLat: parseFloat(import.meta.env.VITE_DEFAULT_MAP_CENTER_LAT) || 5.36,
      centerLng: parseFloat(import.meta.env.VITE_DEFAULT_MAP_CENTER_LNG) || -4.01,
      defaultZoom: parseInt(import.meta.env.VITE_DEFAULT_MAP_ZOOM) || 7
    }
  };

  return config;
}

export function ThemeProvider({ children }) {
  const [isLoading, setIsLoading] = useState(true);
  const [themeLoaded, setThemeLoaded] = useState(false);
  const [dynamicLogoPath, setDynamicLogoPath] = useState(null);
  
  // Get client theme from environment variable or default to 'henddu'
  const clientTheme = import.meta.env.VITE_CLIENT_THEME || 'henddu';
  
  // Get dynamic theme configuration
  const themeConfig = getThemeConfig(clientTheme);

  // Function to get logo path from CSS custom properties
  const getCSSLogoPath = () => {
    if (typeof window !== 'undefined') {
      const computedStyle = getComputedStyle(document.documentElement);
      const cssLogoPath = computedStyle.getPropertyValue('--brand-logo').trim().replace(/['"]/g, '');
      return cssLogoPath || null;
    }
    return null;
  };

  useEffect(() => {
    const loadTheme = async () => {
      try {
        setIsLoading(true);
        
        // Dynamically import the theme CSS using template literal
        const themeModule = await import(`../styles/themes/${clientTheme}.css`);

        // Add theme class to body
        document.body.className = document.body.className.replace(/theme-\w+/g, '');
        document.body.classList.add(`theme-${clientTheme}`);

        // Update favicon if specified
        if (themeConfig.faviconPath) {
          const faviconLink = document.querySelector("link[rel*='icon']") || document.createElement('link');
          faviconLink.type = 'image/x-icon';
          faviconLink.rel = 'shortcut icon';
          faviconLink.href = themeConfig.faviconPath;
          document.getElementsByTagName('head')[0].appendChild(faviconLink);
        }

        // Update page title
        document.title = themeConfig.name;

        // Read logo path from CSS custom properties after theme is loaded
        setTimeout(() => {
          const cssLogoPath = getCSSLogoPath();
          if (cssLogoPath) {
            setDynamicLogoPath(cssLogoPath);
          }
        }, 100); // Small delay to ensure CSS is fully applied

        setThemeLoaded(true);
      } catch (error) {
        console.warn(`⚠️ Theme ${clientTheme} not found, falling back to default`);
        try {
          await import('../styles/themes/henddu.css');
          document.body.classList.add('theme-henddu');
          // Update document title to default config
          document.title = themeConfig.name;
          
          // Try to read logo path from CSS even for fallback theme
          setTimeout(() => {
            const cssLogoPath = getCSSLogoPath();
            if (cssLogoPath) {
              setDynamicLogoPath(cssLogoPath);
            }
          }, 100);
          
          setThemeLoaded(true);
        } catch (fallbackError) {
          console.error('❌ Failed to load fallback theme:', fallbackError);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, [clientTheme]);

  const contextValue = {
    // Theme identification
    clientTheme,
    isDefaultTheme: clientTheme === 'henddu',
    
    // Theme configuration (all from environment variables)
    config: themeConfig,
    
    // Loading states
    isLoading,
    themeLoaded,
    
    // Helper methods
    hasFeature: (featureName) => {
      return themeConfig.features[featureName] || false;
    },
    
    getThemeClass: (baseClass = '') => {
      return `${baseClass} theme-${clientTheme}`.trim();
    },
    
    // Brand information (CSS custom properties take precedence over environment variables)
    brandName: themeConfig.name,
    brandShortName: themeConfig.shortName,
    logoPath: dynamicLogoPath || themeConfig.logoPath,
    supportEmail: themeConfig.supportEmail,
    companyUrl: themeConfig.companyUrl,
    
    // Colors (useful for dynamic styling - will be from CSS variables)
    colors: themeConfig.colors,
    
    // API configuration
    api: themeConfig.api,
    
    // Map configuration  
    map: themeConfig.map
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useClientTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useClientTheme must be used within a ClientThemeProvider');
  }
  return context;
};

// Hook for feature flags
export const useClientFeatures = () => {
  const { config } = useClientTheme();
  return config.features;
};

// Hook for checking specific features
export const useFeature = (featureName) => {
  const { hasFeature } = useClientTheme();
  return hasFeature(featureName);
};

// Export the provider with the correct name
export const ClientThemeProvider = ThemeProvider;

export default ThemeContext;